import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';

export default function GroupDialog({ children, group, users, onSave }) {

    const isEdit = !!group;
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setName(group?.name || '');
            setSelectedUserIds(group?.members?.map((m) => m.userId) || []);
            setError('');
        }
    }, [open, group]);

    const toggleUser = (userId) => {
        setSelectedUserIds((prev) =>
            prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
        );
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            setError('A group name is required.');
            return;
        }
        if (selectedUserIds.length === 0) {
            setError('A group must contain at least one user.');
            return;
        }

        const ok = await onSave({ name: name.trim(), userIds: selectedUserIds });
        if (ok) {
            setOpen(false);
        } else {
            setError('Something went wrong saving the group.');
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent className="sm:max-w-[520px]">
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Edit Group' : 'Create Group'}</DialogTitle>
                    <DialogDescription>
                        Groups bundle users so they can be referenced as workflow approvers.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-4 py-2">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="group-name">Group Name</Label>
                        <Input
                            id="group-name"
                            value={name}
                            placeholder="e.g. Finance Approvers"
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <Label>Members</Label>
                            <Badge variant="secondary">{selectedUserIds.length} selected</Badge>
                        </div>
                        <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 divide-y">
                            {users.length === 0 && (
                                <p className="text-sm text-gray-500 p-3 mb-0">No users available.</p>
                            )}
                            {users.map((u) => (
                                <label
                                    key={u.id}
                                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50"
                                >
                                    <Checkbox
                                        checked={selectedUserIds.includes(u.id)}
                                        onCheckedChange={() => toggleUser(u.id)}
                                    />
                                    <div className="flex flex-col">
                                        <span className="text-sm font-medium">{u.name}</span>
                                        <span className="text-xs text-gray-500">{u.email}</span>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    {error && <p className="text-sm text-red-600 mb-0">{error}</p>}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button className="bg-yavrio-green" onClick={handleSubmit}>
                        {isEdit ? 'Save Group' : 'Create Group'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
